import React, { useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  CircularProgress,
  Alert,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FaHourglassHalf,
  FaChartLine,
  FaCheckCircle,
  FaExclamationCircle,
  FaPauseCircle,
  FaClock,
  FaSync,
} from "react-icons/fa";
import useApiEp from "../../hooks/useApiEp";

const StatCard = styled(Card)(({ theme }) => ({
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[6],
  },
}));

const CompactStatIcon = styled(Box)(({ theme, color }) => ({
  color: color,
  fontSize: "1.5rem",
  marginRight: theme.spacing(2),
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1),
}));

const QueueStatsLive = ({ id, type }) => {
  const theme = useTheme();
  const {
    data: statsD,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/campaign/campaign-stats/${id}`);

  // Calculate campaign status
  const campaignStatus = useMemo(() => {
    if (!statsD) return "Loading...";

    const total = Object.values(statsD).reduce((sum, val) => sum + val, 0);
    if (total === 0) return "Campaign cleared";

    if (statsD.completed > 0 && statsD.completed === total) return "Completed";
    if (statsD.failed > 0 && statsD.failed === total) return "Failed";
    if (statsD.active > 0) return "In Progress";
    if (statsD.waiting > 0) return "Starting";
    if (statsD.paused > 0) return "Paused";

    return "Active";
  }, [statsD]);

  // Calculate total jobs for progress bars
  const totalJobs = useMemo(() => {
    if (!statsD) return 1;
    return Math.max(
      Object.values(statsD).reduce((sum, val) => sum + val, 0),
      1
    );
  }, [statsD]);

  // Auto-refresh every 10 seconds if campaign is active
  // Auto-refresh every 10 seconds if campaign is active
  useEffect(() => {
    // Stop refreshing if stats don't exist yet,
    // or if percentage is 100,
    // or if status indicates completion/failure
    const isFinished =
      statsD?.percentage === 100 ||
      campaignStatus === "Completed" ||
      campaignStatus === "Failed";

    if (!statsD || isFinished) return;

    const interval = setInterval(() => {
      refetch();
    }, 10000);

    return () => clearInterval(interval);
  }, [campaignStatus, statsD?.percentage, refetch]); // Added statsD?.percentage to dependencies

  const statusColors = {
    waiting: theme.palette.info.main,
    active: theme.palette.success.main,
    completed: theme.palette.primary.main,
    failed: theme.palette.error.main,
    delayed: theme.palette.warning.main,
    paused: theme.palette.grey[600],
  };

  const statItems = [
    {
      label: "Waiting",
      value: statsD?.waiting || 0,
      icon: <FaHourglassHalf />,
      color: statusColors.waiting,
      showProgress: true,
      tooltip: "Jobs waiting to be processed",
    },
    {
      label: "Active",
      value: statsD?.active || 0,
      icon: <FaChartLine />,
      color: statusColors.active,
      showProgress: true,
      tooltip: "Jobs currently being processed",
    },
    {
      label: "Completed",
      value: statsD?.completed || 0,
      icon: <FaCheckCircle />,
      color: statusColors.completed,
      showProgress: true,
      tooltip: "Successfully completed jobs",
    },
    {
      label: "Failed",
      value: statsD?.failed || 0,
      icon: <FaExclamationCircle />,
      color: statusColors.failed,
      tooltip: "Jobs that failed during processing",
    },
    {
      label: "Delayed",
      value: statsD?.delayed || 0,
      icon: <FaClock />,
      color: statusColors.delayed,
      tooltip: "Jobs delayed for future processing",
    },
    {
      label: "Paused",
      value: statsD?.paused || 0,
      icon: <FaPauseCircle />,
      color: statusColors.paused,
      tooltip: "Jobs that are currently paused",
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ ml: 2, color: "text.secondary" }}>
          Loading campaign statistics...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle1">
            Error loading statistics: {error?.message || "Unknown error"}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!statsD) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle1">
            No campaign statistics available
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            display: "flex",
            alignItems: "center",
          }}
        >
          {campaignStatus}
          {campaignStatus === "Completed" && (
            <FaCheckCircle
              style={{
                marginLeft: 8,
                color: theme.palette.success.main,
              }}
            />
          )}
          {campaignStatus === "In Progress" && (
            <FaChartLine
              style={{ marginLeft: 8, color: theme.palette.success.main }}
            />
          )}
          {campaignStatus === "Failed" && (
            <FaExclamationCircle
              style={{ marginLeft: 8, color: theme.palette.error.main }}
            />
          )}
        </Typography>

        <Tooltip title="Refresh stats">
          <IconButton
            onClick={refetch}
            disabled={isLoading}
            color="primary"
            size="small"
          >
            <FaSync className={isLoading ? "spin" : ""} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {statItems.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Tooltip title={item.tooltip} arrow>
              <StatCard>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center">
                    <CompactStatIcon color={item.color}>
                      {item.icon}
                    </CompactStatIcon>
                    <Box flexGrow={1}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {item.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  {item.showProgress && (
                    <ProgressContainer>
                      <LinearProgress
                        variant="determinate"
                        value={(item.value / totalJobs) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: theme.palette.grey[200],
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: item.color,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", textAlign: "right", mt: 0.5 }}
                      >
                        {`${Math.round((item.value / totalJobs) * 100)}%`}
                      </Typography>
                    </ProgressContainer>
                  )}
                </CardContent>
              </StatCard>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default QueueStatsLive;
